/**
 * Renders all modals for the CPT codes page: status update, auth
 * inaccuracy, status-changed-to-hold, page-leave confirmation, NAR
 * letter, doc viewer, CPT new description, filing info, and delete confirmation.
 *
 * Each modal is gated by the current `openedModalId` from Redux. The
 * delete-confirmation and add-new-procedure flows are delegated to
 * {@link useJCodeDeleteFlow} and {@link useJCodeAddFlow} respectively,
 * which own the request, abort, and Redux metadata sync.
 *
 * Page-leave save routes through the linked-orders confirmation dialog
 * when filtered sibling orders exist; otherwise it saves CPT changes
 * immediately.
 *
 * @module CptCodesModals
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { MutableRefObject } from "react";
import AddNewCptCodeModal from "../../../../../components/modals/addNewCptCodeModal/addNewCptCodeModal";
import AuthInaccuracyModal from "../../../../../components/modals/authInaccuracyModal/authInaccuracyModal";
import CptNewDescriptionModal from "../../../../../components/modals/cptNewDescriptionModal/cptNewDescriptionModal";
import DeleteConfirmationModal from "../../../../../components/modals/deleteConfirmationModal/deleteConfirmationModal";
import DocViewerModalwithTitle from "../../../../../components/modals/docViewerModalwithTitle/docViewerModalwithTitle";
import FilingInfoModal from "../../../../../components/modals/FilingInfoModal/filingInfoModal";
import { FilingInfoSavePayload } from "../../../../../components/modals/FilingInfoModal/filingInfoModal.utils";
import NarLetterModal from "../../../../../components/modals/narLetterModal/narLetterModal";
import PageLeaveConfirmationModal from "../../../../../components/modals/pageLeaveConfirmationModal/pageLeaveConfirmationModal";
import StatusChangedToHoldModal from "../../../../../components/modals/statusChangedtoHoldModal/statusChangedToHoldModal";
import { StatusUpdateModal } from "../../../../../components/modals/StatusUpdateModal/StatusUpdateModal";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import {
  CptCodesTableRowModel,
  PaRequired,
} from "../../../../../data-model/cptCodesTableModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../../../enums/modalId";
import { useFacilityCategory } from "../../../../../hooks/useOncoemrTasksEnabled";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { useCptLinkedOrdersConfirmationView } from "../hooks/useCptLinkedOrdersConfirmationView";
import { useJCodeAddFlow } from "../hooks/useJCodeAddFlow";
import { useJCodeDeleteFlow } from "../hooks/useJCodeDeleteFlow";
import { logCptNewDescriptionSavedEvent } from "../utils/logCptNewDescriptionSavedEvent";
import { revertTableToLastStateOnClose } from "../utils/revertTableToLastStateIfOnClose";
import { openHoldModalAfterQueryResolution } from "../utils/shouldOpenStatusUpdateModal";

/**
 * Props for {@link CptCodesModals}.
 */
interface CptCodesModalsProps {
  openedModalId: string | null;
  blockerState: "blocked" | "unblocked" | "proceeding";
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  authOnFileDetails: AuthOnFile | null | undefined;
  originalTableData: CptCodesTableRowModel[];
  /** Live CPT rows; used to detect mapped statuses for page-leave linked-order flow. */
  tableData: CptCodesTableRowModel[];
  /** Parent order DOS; scopes linked siblings to later dates when mapped. */
  parentDateOfService?: string;
  hasChanges: boolean;
  hasCptTableAnyError: boolean;
  selectedRowData: CptCodesTableRowModel | null;
  selectedRowDataForReference: MutableRefObject<{
    rowIndex: number;
    fieldKey: string;
    fieldValue: any;
  } | null>;
  isSaving: boolean;
  isHoldSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setTableData: React.Dispatch<React.SetStateAction<CptCodesTableRowModel[]>>;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  saveTableDataToContext: (data: CptCodesTableRowModel[]) => void;
  /** Writes a Filing Info modal save back to the snapshotted CPT row. */
  handleFilingInfoSave: (payload: FilingInfoSavePayload) => void;
  handleSaveChanges: (selectedOrderIds?: string[]) => Promise<void>;
  handleSaveHold: (selectedDate: Date) => Promise<void>;
  handleProceedWithoutSaving: () => void;
  onAuthLetterUploadSuccess: (
    jCodeId: string,
    docPath: string,
    documentTitle: string,
  ) => void;
  onAgentRerunStatusChange?: (
    isRunning: boolean,
    jCodeId: string,
    docPath: string,
  ) => void;
  dispatch: AppDispatch;
}

/**
 * Conditionally renders the modals used on the CPT codes page.
 * Each modal is gated by the current `openedModalId` from Redux.
 *
 * @param props - Modal gate state, order/table data, and save/discard handlers
 * @returns CPT codes modal tree for the single-order worklist page
 */
const CptCodesModals = ({
  openedModalId,
  blockerState,
  generalOrderDetails,
  authOnFileDetails,
  originalTableData,
  tableData,
  parentDateOfService,
  selectedRowData,
  selectedRowDataForReference,
  isSaving,
  isHoldSaving,
  hasChanges,
  hasCptTableAnyError,
  setTableData,
  setHasChanges,
  saveTableDataToContext,
  handleFilingInfoSave,
  handleSaveChanges,
  handleSaveHold,
  handleProceedWithoutSaving,
  onAuthLetterUploadSuccess,
  onAgentRerunStatusChange,
  dispatch,
}: CptCodesModalsProps) => {
  const facilityCategory = useFacilityCategory();
  const { handleDelete, handleCancel } =
    useJCodeDeleteFlow(generalOrderDetails);
  const { handleAdd, handleClose: handleAddClose } =
    useJCodeAddFlow(generalOrderDetails);
  const { hasLinkedOrders } = useCptLinkedOrdersConfirmationView(
    tableData,
    parentDateOfService,
  );

  /**
   * Handles page-leave save: opens linked-orders confirmation when siblings
   * exist, otherwise persists CPT table changes immediately.
   */
  const handlePageLeaveSave = () => {
    if (hasLinkedOrders) {
      dispatch(
        setOpenedModalId({
          id: ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG,
        }),
      );
      return;
    }

    handleSaveChanges();
  };

  const buildRevertParams = () => ({
    rowIndex: selectedRowDataForReference.current?.rowIndex ?? 0,
    fieldKey: selectedRowDataForReference.current?.fieldKey ?? "",
    fieldValue: selectedRowDataForReference.current?.fieldValue ?? {},
    generalOrderDetails: generalOrderDetails ?? null,
    authOnFileDetails: authOnFileDetails ?? null,
    originalTableData,
    setTableData,
    setHasChanges,
    saveTableDataToContext,
    category: facilityCategory,
  });

  return (
    <>
      {openedModalId === ModalId.STATUS_UPDATE_MODAL && (
        <StatusUpdateModal
          onClose={() => {
            dispatch(closeModal());
            revertTableToLastStateOnClose(buildRevertParams());
          }}
          rowData={selectedRowData ?? undefined}
          onSave={() => {
            dispatch(closeModal());
            toast.success(ToastMessages.AUTH_STATUS_CHANGE_REASONS_SAVED);
            openHoldModalAfterQueryResolution(
              selectedRowDataForReference.current?.fieldValue ?? {},
              selectedRowData?.paRequired ?? ({} as PaRequired),
              dispatch,
            );
          }}
        />
      )}
      {openedModalId === ModalId.AUTH_INACCURACY_MODAL && (
        <AuthInaccuracyModal
          onClose={() => dispatch(closeModal())}
          generalOrderDetails={generalOrderDetails ?? null}
          authOnFileDetails={authOnFileDetails ?? null}
        />
      )}
      {openedModalId === ModalId.STATUS_CHANGED_TO_HOLD_MODAL && (
        <StatusChangedToHoldModal
          onClose={() => {
            dispatch(closeModal());
            revertTableToLastStateOnClose(buildRevertParams());
          }}
          rowData={selectedRowData ?? undefined}
          onSave={handleSaveHold}
          loading={isHoldSaving}
        />
      )}
      {blockerState === "blocked" && (
        <PageLeaveConfirmationModal
          onSaveChanges={handlePageLeaveSave}
          onDiscard={handleProceedWithoutSaving}
          saveDisabled={!hasChanges || isSaving || hasCptTableAnyError}
        />
      )}
      {openedModalId === ModalId.NAR_LETTER_MODAL && (
        <NarLetterModal
          onClose={() => dispatch(closeModal())}
          showDragDropUpload={true}
          shouldLogEvent={true}
          onSuccess={onAuthLetterUploadSuccess}
          onAgentRerunStatusChange={onAgentRerunStatusChange}
        />
      )}
      {openedModalId === ModalId.DOC_VIEWER_MODAL_WITH_TITLE && (
        <DocViewerModalwithTitle onClose={() => dispatch(closeModal())} />
      )}
      {openedModalId === ModalId.CPT_NEW_DESCRIPTION_MODAL && (
        <CptNewDescriptionModal
          onCancel={() => {
            dispatch(closeModal());
            revertTableToLastStateOnClose(buildRevertParams());
          }}
          onClose={() => {
            dispatch(closeModal());
            revertTableToLastStateOnClose(buildRevertParams());
          }}
          onSave={(description, _id, cptCode) => {
            logCptNewDescriptionSavedEvent({
              cptDescription: description ?? "",
              cptCode: cptCode ?? "",
              entryFlag: "new",
              generalOrderDetails,
            });
            dispatch(closeModal());
          }}
        />
      )}
      {openedModalId === ModalId.DELETE_CONFIRMATION_MODAL && (
        <DeleteConfirmationModal
          onDeleteClick={handleDelete}
          onCancelClick={handleCancel}
          onCloseClick={handleCancel}
        />
      )}
      {openedModalId === ModalId.ADD_NEW_CPT_CODE_MODAL && (
        <AddNewCptCodeModal onClose={handleAddClose} onSave={handleAdd} />
      )}
      {openedModalId === ModalId.FILING_INFO_MODAL && (
        <FilingInfoModal
          onClose={() => dispatch(closeModal())}
          initialFilingMode={selectedRowData?.filingInfo?.value ?? ""}
          initialFilingDetail={selectedRowData?.filingInfo?.detail ?? ""}
          // Auth transition pair for getFilingInfoRequired save gating in the modal.
          oldAuthStatusId={selectedRowData?.filingInfo?.priorAuthStatusId}
          newAuthStatusId={selectedRowData?.paRequired?.currentStatusId}
          onSave={handleFilingInfoSave}
        />
      )}
    </>
  );
};

export default CptCodesModals;
